/**
 * VirtClustersList — Clusters de virtualización (estándar de listados del CMDB).
 *
 * Antes: tabla propia con tema oscuro a mano, la tabla de nodos desplegable
 * dentro de la fila (y sin los datos del nodo: el backend no mandaba `mgmt_ip`,
 * así que la columna IP salía siempre vacía), diálogo de borrado propio y ficha
 * abriendo en edición.
 *
 * Ahora: `ListPage` con buscador, filtros, columnas, vistas y export; la ficha
 * entra en **lectura**. Los nodos pasan a un **diálogo** propio con alta y baja,
 * que es donde tienen sentido (y así la tabla del listado no se rompe).
 *
 * Botones propios:
 *  · **Ver los nodos** — diálogo con los hosts del cluster, su IP, su agente y
 *    su rol, con alta y baja.
 *  · **Ver sus hipervisores** — a Hipervisores con `?clusterId=`.
 *  · **Ver sus VMs** — a Máquinas virtuales con `?clusterId=`.
 *  · **Ver sus vSwitches distribuidos** — a vSwitches con `?clusterId=
 *    &tipo=distributed`.
 *  · **Copiar** — alta precargada, sin nodos.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Squares2X2Icon,
  ListBulletIcon,
  ServerStackIcon,
  ComputerDesktopIcon,
  ArrowsRightLeftIcon,
  PlusIcon,
  XMarkIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import VirtClusterForm from './VirtClusterForm';
import { useListView } from '../views/ViewsContext';
import useNavOrder from '../hooks/useNavOrder';
import { getServerFormStyle } from '../utils/serverFormStyle';
import api from '../services/api';

const STATUS_META = {
  active:      { label: 'Activo',        badge: 'list-badge--on' },
  degraded:    { label: 'Degradado',     badge: 'list-badge--warn' },
  maintenance: { label: 'Mantenimiento', badge: 'list-badge--warn' },
  decom:       { label: 'Decom',         badge: 'list-badge--off' },
};

const CLUSTER_TYPE_LABEL = {
  vmware_vsphere:    'VMware vSphere',
  proxmox:           'Proxmox VE Cluster',
  hyper_v_failover:  'Hyper-V Failover',
  nutanix:           'Nutanix',
  kvm_ovirt:         'KVM / oVirt',
  other:             'Otro',
};

const MEMBER_ROLES = [
  { value: 'master',  label: 'Master / vCenter' },
  { value: 'worker',  label: 'Worker' },
  { value: 'witness', label: 'Witness' },
];

const AGENT_LABEL = {
  online:     'Agente online',
  offline:    'Agente offline',
  'no-agent': 'Sin agente',
  unknown:    'Sin datos',
};

const EMPTY_CLUSTER = {
  name: '', cluster_type: 'vmware_vsphere', version: '', mgmt_ip: '', mgmt_url: '',
  total_nodes: '', is_hyperconverged: false, ha_enabled: false, drs_enabled: false,
  status: 'active', description: '', notes: '',
};

export default function VirtClustersList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems]             = useState([]);
  const [hypervisors, setHypervisors] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [search, setSearch]           = useState('');

  const [filterType, setFilterType]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterHa, setFilterHa]         = useState('');
  const [filterDrs, setFilterDrs]       = useState('');
  const [filterHci, setFilterHci]       = useState('');
  const [filterNodes, setFilterNodes]   = useState('');
  const [filterVms, setFilterVms]       = useState('');

  const [selected, setSelected]       = useState(null);
  const [initialMode, setInitialMode] = useState('edit');
  const [cloneSource, setCloneSource] = useState(null);
  const [hlId, setHlId]               = useState(null);

  /* Diálogo de nodos */
  const [nodesOf, setNodesOf]               = useState(null);
  const [addMember, setAddMember]           = useState({ hypId: '', role: 'worker' });
  const [memberError, setMemberError]       = useState('');
  const [memberSaving, setMemberSaving]     = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [cls, hyps] = await Promise.all([
        api.getClusters(companyId),
        api.getHypervisors(companyId).catch(() => ({ data: [] })),
      ]);
      setItems(cls.data || []);
      setHypervisors(hyps.data || []);
    } catch (e) {
      setError(e.message || 'Error cargando los clusters');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ?destacar=<id>[&abrir=1] */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !items.length) return undefined;
    const id = Number(raw);
    const target = items.find(c => c.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setCloneSource(null); setInitialMode('view'); setSelected(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`virt-clu-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, items, setSearchParams]);

  /* ── ficha ──────────────────────────────────────────────────────────────── */
  const openView   = (c) => { setCloneSource(null); setInitialMode('view');   setSelected(c); };
  const openEdit   = (c) => { setCloneSource(null); setInitialMode('edit');   setSelected(c); };
  const openCreate = ()  => { setCloneSource(null); setInitialMode('create'); setSelected({ ...EMPTY_CLUSTER }); };
  const openCopy   = (c) => {
    if (!c) return;
    setSelected(null);
    setCloneSource({
      ...c,
      id: undefined,
      name: c.name ? `${c.name} (copia)` : '',
      mgmt_ip: '',
      mgmt_url: '',
      nodes: [],
      node_count: 0,
      vm_count: 0,
    });
    setInitialMode('create');
  };
  const closeForm = () => { setSelected(null); setCloneSource(null); load(); };

  const handleDelete = async (c) => {
    try {
      await api.deleteCluster(c.id, companyId);
      await load();
    } catch (e) {
      setError(e.message || 'Error eliminando el cluster');
    }
  };

  /* ── nodos (alta y baja desde el diálogo) ───────────────────────────────── */
  const openNodes = (c) => { setNodesOf(c); setAddMember({ hypId: '', role: 'worker' }); setMemberError(''); };
  const closeNodes = () => { setNodesOf(null); setMemberError(''); };

  const handleAddMember = async () => {
    if (!addMember.hypId || !nodesOf) return;
    setMemberSaving(true);
    setMemberError('');
    try {
      await api.addClusterMember(
        nodesOf.id,
        { hypervisor_id: Number(addMember.hypId), member_role: addMember.role },
        companyId,
      );
      const fresh = await api.getClusters(companyId);
      const updated = (fresh.data || []).find(c => c.id === nodesOf.id) || null;
      setItems(fresh.data || []);
      setNodesOf(updated);
      setAddMember({ hypId: '', role: 'worker' });
    } catch (e) {
      setMemberError(e.message || 'No se pudo añadir el nodo');
    } finally {
      setMemberSaving(false);
    }
  };

  const handleRemoveMember = async (hypervisorId) => {
    if (!nodesOf) return;
    setMemberSaving(true);
    setMemberError('');
    try {
      await api.removeClusterMember(nodesOf.id, hypervisorId, companyId);
      const fresh = await api.getClusters(companyId);
      const updated = (fresh.data || []).find(c => c.id === nodesOf.id) || null;
      setItems(fresh.data || []);
      setNodesOf(updated);
    } catch (e) {
      setMemberError(e.message || 'No se pudo quitar el nodo');
    } finally {
      setMemberSaving(false);
    }
  };

  /* ── filtrado ───────────────────────────────────────────────────────────── */
  const typeOptions = useMemo(() => {
    const seen = new Map();
    items.forEach((c) => {
      if (c.cluster_type) seen.set(c.cluster_type, c.cluster_type_label || CLUSTER_TYPE_LABEL[c.cluster_type] || c.cluster_type);
    });
    Object.entries(CLUSTER_TYPE_LABEL).forEach(([value, label]) => {
      if (!seen.has(value)) seen.set(value, label);
    });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [items]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((c) => {
      if (filterType && c.cluster_type !== filterType) return false;
      if (filterStatus && (c.status || 'active') !== filterStatus) return false;
      if (filterHa === 'yes' && !c.ha_enabled) return false;
      if (filterHa === 'no' && c.ha_enabled) return false;
      if (filterDrs === 'yes' && !c.drs_enabled) return false;
      if (filterDrs === 'no' && c.drs_enabled) return false;
      if (filterHci === 'yes' && !c.is_hyperconverged) return false;
      if (filterHci === 'no' && c.is_hyperconverged) return false;
      if (filterNodes === 'incomplete' && !(c.total_nodes && (c.node_count || 0) < c.total_nodes)) return false;
      if (filterVms === 'with' && !(c.vm_count > 0)) return false;
      if (filterVms === 'without' && c.vm_count > 0) return false;
      if (!needle) return true;
      return [
        c.name, c.mgmt_ip, c.mgmt_url, c.description,
        c.cluster_type_label || CLUSTER_TYPE_LABEL[c.cluster_type] || c.cluster_type,
        (c.nodes || []).map(n => n.hypervisor_name).filter(Boolean).join(' '),
      ].filter(Boolean).join(' ').toLowerCase().includes(needle);
    });
  }, [items, search, filterType, filterStatus, filterHa, filterDrs, filterHci, filterNodes, filterVms]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navClusters = useNavOrder(filtered, selected, selected !== null);

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: tr('virt.name', 'Nombre'),
        sortable: true,
        render: (c) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{c.name}</span>
            <span className="list-hint">
              {c.cluster_type_label || CLUSTER_TYPE_LABEL[c.cluster_type] || c.cluster_type}
              {c.version ? ` · v${c.version}` : ''}
            </span>
          </span>
        ),
      },
      {
        key: 'type',
        label: tr('virt.type', 'Tipo'),
        sortable: true,
        sortValue: (c) => c.cluster_type || '',
        render: (c) => (
          <span className="list-badge">
            {c.cluster_type_label || CLUSTER_TYPE_LABEL[c.cluster_type] || c.cluster_type || dash}
          </span>
        ),
      },
      {
        key: 'mgmt_ip',
        label: tr('virt.mgmt_ip', 'IP de gestión'),
        sortable: true,
        render: (c) => (c.mgmt_ip ? <span className="list-code">{c.mgmt_ip}</span> : dash),
      },
      {
        key: 'node_count',
        label: tr('virt.nodes', 'Nodos'),
        sortable: true,
        sortValue: (c) => c.node_count ?? 0,
        render: (c) => {
          const count = c.node_count || 0;
          const total = c.total_nodes || 0;
          const incomplete = total > 0 && count < total;
          return (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: 'pointer', color: incomplete ? 'var(--amber-600)' : undefined }}
              onClick={(event) => { event.stopPropagation(); openNodes(c); }}
              title={tr('virt.view_nodes', 'Ver los nodos del cluster')}
            >
              {count}{total ? ` / ${total}` : ''}
            </button>
          );
        },
      },
      {
        key: 'features',
        label: tr('virt.features', 'Características'),
        sortValue: (c) => [c.is_hyperconverged, c.ha_enabled, c.drs_enabled].filter(Boolean).length,
        render: (c) => {
          const bits = [];
          if (c.is_hyperconverged) bits.push(<span key="hci" className="list-badge">HCI</span>);
          if (c.ha_enabled) bits.push(<span key="ha" className="list-badge list-badge--on">HA</span>);
          if (c.drs_enabled) bits.push(<span key="drs" className="list-badge">DRS</span>);
          return bits.length
            ? <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>{bits}</span>
            : dash;
        },
      },
      {
        key: 'vm_count',
        label: tr('virt.vms', 'VMs'),
        sortable: true,
        sortValue: (c) => c.vm_count ?? 0,
        render: (c) => (
          <button
            type="button"
            className="list-badge"
            style={{ cursor: c.vm_count ? 'pointer' : 'default' }}
            disabled={!c.vm_count}
            onClick={(event) => {
              if (!c.vm_count) return;
              event.stopPropagation();
              navigate(`/virt/vms?clusterId=${c.id}`);
            }}
            title={tr('virt.view_vms', 'Ver sus VMs')}
          >
            {c.vm_count || 0} VMs
          </button>
        ),
      },
      {
        key: 'status',
        label: tr('ipam.status', 'Estado'),
        sortable: true,
        sortValue: (c) => c.status || 'active',
        render: (c) => {
          const meta = STATUS_META[c.status] || STATUS_META.active;
          return <span className={`list-badge ${meta.badge}`}>{meta.label}</span>;
        },
      },
      {
        key: 'description',
        label: tr('description', 'Descripción'),
        render: (c) => c.description || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, navigate]);

  const defaultVisible = ['name', 'type', 'mgmt_ip', 'node_count', 'features', 'vm_count', 'status'];

  const filters = [
    {
      key: 'type', label: tr('virt.type', 'Tipo'), allLabel: tr('virt.type_all', 'Todos los tipos'),
      value: filterType, onChange: setFilterType, options: typeOptions,
    },
    {
      key: 'status', label: tr('ipam.status', 'Estado'), allLabel: tr('ipam.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'ha', label: 'HA', allLabel: tr('virt.ha_all', 'HA: cualquiera'),
      value: filterHa, onChange: setFilterHa,
      options: [
        { value: 'yes', label: tr('virt.ha_yes', 'Con HA activo') },
        { value: 'no', label: tr('virt.ha_no', 'Sin HA') },
      ],
    },
    {
      key: 'drs', label: 'DRS', allLabel: tr('virt.drs_all', 'DRS: cualquiera'),
      value: filterDrs, onChange: setFilterDrs,
      options: [
        { value: 'yes', label: tr('virt.drs_yes', 'Con DRS activo') },
        { value: 'no', label: tr('virt.drs_no', 'Sin DRS') },
      ],
    },
    {
      key: 'hci', label: tr('virt.hci', 'Hiperconvergente'), allLabel: tr('virt.hci_all', 'HCI: cualquiera'),
      value: filterHci, onChange: setFilterHci,
      options: [
        { value: 'yes', label: tr('virt.hci_yes', 'Solo hiperconvergentes') },
        { value: 'no', label: tr('virt.hci_no', 'Solo no hiperconvergentes') },
      ],
    },
    {
      key: 'nodes', label: tr('virt.nodes', 'Nodos'), allLabel: tr('virt.nodes_all', 'Cualquier número de nodos'),
      value: filterNodes, onChange: setFilterNodes,
      options: [
        { value: 'incomplete', label: tr('virt.nodes_incomplete', 'Con nodos pendientes de asignar') },
      ],
    },
    {
      key: 'vms', label: tr('virt.vms', 'VMs'), allLabel: tr('virt.vms_all', 'Cualquier número de VMs'),
      value: filterVms, onChange: setFilterVms,
      options: [
        { value: 'with', label: tr('virt.vms_with', 'Con VMs') },
        { value: 'without', label: tr('virt.vms_without', 'Sin VMs') },
      ],
    },
  ];

  const subtitle = `${items.length} clusters`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  const exportRows = useMemo(() => filtered.map(c => ({
    'Nombre': c.name || '',
    'Tipo': c.cluster_type_label || CLUSTER_TYPE_LABEL[c.cluster_type] || c.cluster_type || '',
    'Versión': c.version || '',
    'IP de gestión': c.mgmt_ip || '',
    'URL de gestión': c.mgmt_url || '',
    'Nodos asignados': c.node_count ?? 0,
    'Nodos previstos': c.total_nodes ?? '',
    'Nodos (detalle)': (c.nodes || []).map(n => n.hypervisor_name).filter(Boolean).join(' · '),
    'Hiperconvergente': c.is_hyperconverged ? 'Sí' : 'No',
    'HA': c.ha_enabled ? 'Sí' : 'No',
    'DRS': c.drs_enabled ? 'Sí' : 'No',
    'VMs': c.vm_count ?? 0,
    'Descripción': c.description || '',
    'Estado': (STATUS_META[c.status] || STATUS_META.active).label,
  })), [filtered]);

  /** Tarjetas: lo esencial del cluster, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((c) => {
          const meta = STATUS_META[c.status] || STATUS_META.active;
          const total = c.total_nodes || 0;
          const incomplete = total > 0 && (c.node_count || 0) < total;
          return (
            <div
              key={c.id}
              id={`virt-clu-card-${c.id}`}
              className={`list-card${c.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openView(c)}
              onKeyDown={(event) => { if (event.key === 'Enter') openView(c); }}
              title={`${tr('action.view', 'Ver')} ${c.name}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">{c.name}</div>
                  {c.mgmt_ip && <span className="list-code">{c.mgmt_ip}</span>}
                </div>
                <span className={`list-badge ${meta.badge}`}>{meta.label}</span>
              </div>

              <div className="list-card-meta">
                <span className="list-badge">
                  {c.cluster_type_label || CLUSTER_TYPE_LABEL[c.cluster_type] || c.cluster_type}
                </span>
                <span className={incomplete ? 'list-badge list-badge--warn' : 'list-badge'}>
                  {c.node_count || 0}{total ? ` / ${total}` : ''} nodos
                </span>
                {c.is_hyperconverged && <span className="list-badge">HCI</span>}
                {c.ha_enabled && <span className="list-badge list-badge--on">HA</span>}
                {c.drs_enabled && <span className="list-badge">DRS</span>}
                <span className="list-badge">{c.vm_count || 0} VMs</span>
              </div>

              <div className="list-card-actions">{rowActions(c)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  const CLUSTER_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada cluster'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId]);
  useListView('virt_clusters', 'list');

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('virt.select_company', 'Selecciona una empresa para ver la virtualización.')}
        </div>
      </div>
    );
  }

  const availableNodes = (nodesOf
    ? hypervisors.filter(h => !(nodesOf.nodes || []).some(n => n.hypervisor_id === h.id))
    : []);

  return (
    <>
      {(selected !== null || cloneSource) && (
        <VirtClusterForm
          cluster={cloneSource || (selected && selected.id ? selected : null)}
          companyId={companyId}
          initialMode={cloneSource ? 'create' : initialMode}
          variant={getServerFormStyle() === 'cards' ? 'cards' : 'classic'}
          navItems={navClusters}
          navIndex={!cloneSource && selected && selected.id ? navClusters.findIndex(c => String(c.id) === String(selected.id)) : -1}
          onNavigate={(destino) => setSelected(destino)}
          onClose={closeForm}
          onSave={(creado) => {
            load();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setInitialMode('edit'); setSelected(creado); }
          }}
        />
      )}

      <div style={{ display: (selected !== null || cloneSource) ? 'none' : undefined }}>
        <ListPage
          icon={Squares2X2Icon}
          title={tr('virt.cluster_title', 'Clusters de virtualización')}
          subtitle={subtitle}

          onCreate={openCreate}
          createLabel={tr('virt.new_cluster', 'Nuevo cluster')}
          createTitle={tr('virt.new_cluster_hint', 'Agrupa hipervisores para gestionar HA, DRS y vMotion')}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('virt.cluster_search', 'Buscar por nombre, IP, tipo, nodo…')}
          filters={filters}

          exportRows={exportRows}
          exportFileBase="clusters"
          exportSheetName="Clusters"

          columns={columns}
          columnStorageKey="cmdb_cols_virt_clusters"
          companyId={companyId}
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(c) => c.id}
          rowId={(c) => `virt-clu-row-${c.id}`}
          rowClassName={(c) => (c.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openView}
          rowTitle={(c) => `${tr('action.view', 'Ver')} ${c.name}`}

          views={CLUSTER_VIEWS}
          listKey="virt_clusters"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando clusters…')}
          error={error}

          empty={{
            icon: '🧩',
            title: items.length === 0
              ? tr('virt.cluster_empty', 'No hay clusters')
              : tr('msg.no_results', 'Ningún cluster coincide con los filtros'),
            subtitle: items.length === 0
              ? tr('virt.cluster_empty_hint', 'Crea un cluster para agrupar hipervisores y activar HA / DRS.')
              : undefined,
            action: items.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('virt.new_cluster', 'Nuevo cluster')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (c) => (
              <>
                {/* Ver los nodos (alta y baja dentro del diálogo) */}
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => openNodes(c)}
                  title={tr('virt.view_nodes', 'Ver los nodos del cluster')}
                >
                  <ServerStackIcon />
                </button>
                {/* Ver sus hipervisores */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!(c.node_count > 0)}
                  onClick={() => navigate(`/virt/hypervisors?clusterId=${c.id}`)}
                  title={tr('virt.view_hypervisors', 'Ver sus hipervisores')}
                >
                  <CircleStackIcon />
                </button>
                {/* Ver sus VMs */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!(c.vm_count > 0)}
                  onClick={() => navigate(`/virt/vms?clusterId=${c.id}`)}
                  title={tr('virt.view_vms', 'Ver sus VMs')}
                >
                  <ComputerDesktopIcon />
                </button>
                {/* Ver sus vSwitches distribuidos */}
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => navigate(`/virt/vswitches?clusterId=${c.id}&tipo=distributed`)}
                  title={tr('virt.view_distributed', 'Ver sus vSwitches distribuidos')}
                >
                  <ArrowsRightLeftIcon />
                </button>
              </>
            ),
            onCopy: openCopy,
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('virt.cluster_delete_title', 'Eliminar cluster'),
            deleteMessage: (c) => (
              <>
                {tr('virt.delete_confirm', '¿Seguro que quieres eliminar')} <strong>{c.name}</strong>?{' '}
                {c.node_count > 0
                  ? tr('virt.cluster_delete_used', `Se perderán las asignaciones de sus ${c.node_count} nodo(s).`)
                  : tr('ip.delete_warn', 'No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>

      {/* ══ Diálogo de nodos ══ */}
      {nodesOf && (
        <Dialog
          title={`${tr('virt.nodes_of', 'Nodos de')} ${nodesOf.name}`}
          icon={<ServerStackIcon style={{ width: 18, height: 18 }} />}
          maxWidth={720}
          onClose={closeNodes}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={closeNodes}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          <table className="table table-sm table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>{tr('virt.hypervisor', 'Hipervisor')}</th>
                <th>{tr('virt.mgmt_ip', 'IP de gestión')}</th>
                <th>{tr('virt.agent', 'Agente')}</th>
                <th>{tr('virt.role', 'Rol')}</th>
                <th className="list-th-cog" />
              </tr>
            </thead>
            <tbody>
              {(nodesOf.nodes || []).map((n) => (
                <tr key={n.hypervisor_id}>
                  <td>
                    <button
                      type="button"
                      className="list-badge"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/virt/hypervisors?destacar=${n.hypervisor_id}&abrir=1`)}
                      title={tr('virt.view_hypervisor', 'Ver el hipervisor')}
                    >
                      {n.hypervisor_name || `#${n.hypervisor_id}`}
                    </button>
                  </td>
                  <td>{n.mgmt_ip ? <span className="list-code">{n.mgmt_ip}</span> : <span className="list-hint">—</span>}</td>
                  <td>
                    <span className="list-badge">
                      {(AGENT_LABEL[n.agent_status] || AGENT_LABEL.unknown)}
                    </span>
                  </td>
                  <td>
                    <span className="list-badge">
                      {MEMBER_ROLES.find(r => r.value === n.member_role)?.label || n.member_role || '—'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="list-action-btn list-action-btn--danger"
                      disabled={memberSaving}
                      onClick={() => handleRemoveMember(n.hypervisor_id)}
                      title={tr('virt.remove_node', 'Quitar del cluster')}
                    >
                      <XMarkIcon />
                    </button>
                  </td>
                </tr>
              ))}
              {(nodesOf.nodes || []).length === 0 && (
                <tr>
                  <td colSpan={5} className="list-hint" style={{ textAlign: 'center' }}>
                    {tr('virt.no_nodes', 'Sin nodos asignados todavía.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Alta de nodo */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
            <select
              className="list-select"
              style={{ maxWidth: 280 }}
              value={addMember.hypId}
              onChange={(e) => setAddMember(a => ({ ...a, hypId: e.target.value }))}
              disabled={memberSaving}
            >
              <option value="">{tr('virt.pick_hypervisor', '— Seleccionar hipervisor —')}</option>
              {availableNodes.map(h => (
                <option key={h.id} value={h.id}>
                  {h.name}{h.mgmt_ip ? ` · ${h.mgmt_ip}` : ''}{h.cluster_name ? ` (${h.cluster_name})` : ''}
                </option>
              ))}
            </select>
            <select
              className="list-select"
              style={{ maxWidth: 170 }}
              value={addMember.role}
              onChange={(e) => setAddMember(a => ({ ...a, role: e.target.value }))}
              disabled={memberSaving}
            >
              {MEMBER_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <button
              type="button"
              className="list-btn-primary"
              onClick={handleAddMember}
              disabled={memberSaving || !addMember.hypId}
            >
              <PlusIcon style={{ width: 14, height: 14 }} /> {tr('virt.add_node', 'Añadir nodo')}
            </button>
          </div>
          {availableNodes.length === 0 && (
            <p className="list-hint" style={{ marginTop: 6, marginBottom: 0 }}>
              {tr('virt.all_nodes_added', 'No quedan hipervisores libres en esta empresa para añadir a este cluster.')}
            </p>
          )}
          {memberError && <div className="alert alert-danger py-2 mt-2 mb-0">{memberError}</div>}
        </Dialog>
      )}
    </>
  );
}
